/** Date-range helpers for create/assign. Backend rejects overlapping active plans. */

export const PLAN_DURATION_DAYS = 30;

export type OccupiedRange = {
  id?: string;
  name: string;
  from: string;
  to: string;
};

const BLOCKING_STATUS = new Set(['pending', 'approved', 'draft', 'published']);

const isoDay = (iso?: string | null) => String(iso || '').slice(0, 10);

/** Inclusive overlap of YYYY-MM-DD ranges. */
export const rangesOverlap = (aFrom: string, aTo: string, bFrom: string, bTo: string) =>
  !!aFrom && !!aTo && !!bFrom && !!bTo && aFrom <= bTo && bFrom <= aTo;

export const occupiedRanges = (
  plans: Array<{
    diet_plans_id?: string | null;
    diet_plan_id?: string | null;
    plan_name?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    status?: string | null;
  }> = [],
  exceptId?: string | number | null,
): OccupiedRange[] => {
  const skip = exceptId != null ? String(exceptId) : '';
  return plans.flatMap((p) => {
    const id = String(p.diet_plans_id || p.diet_plan_id || '');
    if (skip && id === skip) return [];
    const status = String(p.status || '').toLowerCase();
    if (status && !BLOCKING_STATUS.has(status)) return [];
    const from = isoDay(p.start_date);
    const to = isoDay(p.end_date);
    if (!from || !to) return [];
    return [{ id: id || undefined, name: p.plan_name || 'Existing plan', from, to }];
  });
};

export const findOverlap = (
  from: string,
  to: string,
  occupied: OccupiedRange[],
): OccupiedRange | null => occupied.find((r) => rangesOverlap(from, to, r.from, r.to)) ?? null;

export const defaultPlanRange = (
  from: string,
  durationDays = PLAN_DURATION_DAYS,
  plusDays: (iso: string, d: number) => string,
) => ({ from, to: plusDays(from, durationDays) });

/** First `durationDays` window starting on/after `from` that does not overlap occupied ranges. */
export const nextFreeRange = (
  occupied: OccupiedRange[],
  from: string,
  durationDays = PLAN_DURATION_DAYS,
  plusDays: (iso: string, d: number) => string,
): { from: string; to: string } => {
  let start = from;
  for (let i = 0; i < 48; i += 1) {
    const to = plusDays(start, durationDays);
    const hit = findOverlap(start, to, occupied);
    if (!hit) return { from: start, to };
    const next = plusDays(hit.to, 1);
    start = next > start ? next : plusDays(start, 1);
  }
  return defaultPlanRange(start, durationDays, plusDays);
};
